/**
 * <BadgedIconButton> — un bouton d'icône qui porte un compte ou une pastille (molecule).
 *
 * Le chat d'un live et ses messages non lus, la todo d'une activité faite en live et son nombre
 * d'activités, « Un problème ? » quand l'écran en a détecté un : le même geste, un bouton rond et,
 * à son coin haut droit, la chip du livre (`<Badge>`, « 9+ » au-delà de neuf) ou le point
 * (`<NotificationDot>`) quand il n'y a rien à compter. `<HelpBubble>` fait la même chose pour le
 * support, avec ses propres couleurs.
 *
 * Le compte déborde du bouton : la ligne qui le contient lui laisse un peu de place en haut.
 *
 * Ce qui se dessine se dit : le compte, ou le point, suit le nom du bouton pour un lecteur
 * d'écran (« Chat, 2 messages non lus »). La chip et le point, eux, ne se lisent pas.
 *
 * `push` en fait un poussoir carré du livre (`<PushButton iconOnly>`, sa face et son relief) au lieu du bouton rond :
 * la barre du bas du live du masteur (Wajih, 05/11 : « des boutons-icônes du DS »).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge, type BadgeVariant } from "../../atoms/Badge"
import {
    type ButtonSize,
    PushButton,
    type PushFace,
    pushButtonBoxHeight,
    pushButtonFaceHeight,
} from "../../atoms/Buttons/Button/Button"
import { IconButton, type IconButtonProps } from "../../atoms/Buttons/IconButton"
import { NotificationDot } from "../../atoms/NotificationDot"
import { cappedCount } from "../../utils/cappedCount"

export type BadgedIconButtonProps = IconButtonProps & {
    /** Le compte, dans la chip. `0` ou absent : pas de chip. */
    count?: number
    /** La chip du compte — défaut `danger`, les non-lus ; `neutral` pour un simple nombre. */
    countVariant?: BadgeVariant
    /** Le point, quand il n'y a rien à compter. Ignoré sous un compte. Défaut `false`. */
    dot?: boolean
    /**
     * Ce que le compte ajoute au nom du bouton (« 2 messages non lus »). Défaut « 2 non lus ».
     * `null` quand le nom le dit déjà (« Mes activités, 3 activités »).
     */
    countLabel?: ((count: number) => string) | null
    /** Ce que le point ajoute au nom du bouton. Défaut « à voir ». */
    dotLabel?: string
    /** Un poussoir carré, sa face et sa taille, au lieu du bouton rond (`size` est alors ignoré). Absent : le rond. */
    push?: { face: PushFace; size: ButtonSize }
}

const DEFAULT_COUNT_LABEL = (count: number) => (count === 1 ? "1 non lu" : `${count} non lus`)
const DEFAULT_DOT_LABEL = "à voir"

export function BadgedIconButton(props: BadgedIconButtonProps) {
    const {
        count = 0,
        countVariant = "danger",
        dot = false,
        countLabel = DEFAULT_COUNT_LABEL,
        dotLabel = DEFAULT_DOT_LABEL,
        size = 50,
        push,
        ...button
    } = props

    const mark = count > 0 ? (countLabel != null ? countLabel(count) : null) : dot ? dotLabel : null
    const accessibilityLabel =
        mark != null && button.accessibilityLabel != null
            ? `${button.accessibilityLabel}, ${mark}`
            : button.accessibilityLabel

    return (
        <View
            style={
                push != null
                    ? styles.box(pushButtonFaceHeight(push.size), pushButtonBoxHeight(push.size))
                    : styles.box(size, size)
            }
        >
            {push != null ? (
                <PushButton
                    iconOnly
                    label={button.accessibilityLabel ?? ""}
                    iconName={button.iconName}
                    face={push.face}
                    size={push.size}
                    loading={button.loading}
                    disabled={button.disabled}
                    onPress={button.onPress}
                    accessibilityLabel={accessibilityLabel}
                    testID={button.testID}
                />
            ) : (
                <IconButton {...button} size={size} accessibilityLabel={accessibilityLabel} />
            )}
            {count > 0 ? (
                <View style={styles.count} pointerEvents="none">
                    <Badge text={cappedCount(count)} variant={countVariant} />
                </View>
            ) : (
                dot && (
                    <View style={styles.dot} pointerEvents="none">
                        <NotificationDot />
                    </View>
                )
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    box: (width: number, height: number) => ({ width, height }),
    count: {
        position: "absolute",
        top: -theme.spacing["2"],
        right: -theme.spacing["2"],
    },
    dot: {
        position: "absolute",
        top: 0,
        right: 0,
    },
}))
